import api from 'api'
import {
  CACHE_TAG_ACCOUNT_PAYMENT_DETAILS,
  CACHE_TAG_ACCOUNT_PAYMENT_METHOD_LIST,
  CACHE_TAG_ACCOUNT_PAYMENT_TRANSACTION_LIST,
} from 'api/cacheTagTypes'

import Constants from 'Constants'

const { SERVER_PAYMENTS_URL } = Constants

const extendedApi = api.injectEndpoints({
  endpoints: builder => ({
    getPGUserInfo: builder.query({
      // accountId optional: with it the gateway fields resolve from the account's business-unit
      // connector; without it they fall back to the tenant-level connector.
      query: ({ businessId, accountId }) => ({
        url: `${SERVER_PAYMENTS_URL}/api/portal/tenants/${businessId}/user/info`,
        method: 'POST',
        body: accountId ? { accountId } : {},
      }),
    }),
    getIsSurchargeApplied: builder.query({
      query: ({ businessId }) => ({
        url: `${SERVER_PAYMENTS_URL}/api/portal/tenants/${businessId}/payment/surcharge`,
        method: 'GET',
      }),
    }),
    getSurchargeRate: builder.query({
      query: ({ businessId, zip, token }) => ({
        url: `${SERVER_PAYMENTS_URL}/api/portal/tenants/${businessId}/payment/surcharge/rate`,
        method: 'GET',
        // Token is required: Fortis returns surcharge_applied=false without one, and the rate is
        // token-specific (debit/ACH come back not-applied). Not cached on the backend.
        params: { zip, ...(token ? { token } : {}) },
      }),
    }),
    getAllPaymentMethods: builder.query({
      query: ({ businessId, accountId }) => ({
        url: `${SERVER_PAYMENTS_URL}/api/portal/tenants/${businessId}/payment-method/list`,
        method: 'POST',
        body: { accountId },
      }),
      providesTags: (result, error, { accountId }) => [
        CACHE_TAG_ACCOUNT_PAYMENT_METHOD_LIST,
        { type: CACHE_TAG_ACCOUNT_PAYMENT_METHOD_LIST, id: accountId },
      ],
    }),
    addPaymentMethod: builder.mutation({
      query: ({ businessId, payload }) => ({
        url: `${SERVER_PAYMENTS_URL}/api/portal/tenants/${businessId}/payment-method/add`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [CACHE_TAG_ACCOUNT_PAYMENT_METHOD_LIST],
    }),
    removePaymentMethod: builder.mutation({
      query: ({ businessId, payload }) => ({
        url: `${SERVER_PAYMENTS_URL}/api/portal/tenants/${businessId}/payment-method/remove`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [CACHE_TAG_ACCOUNT_PAYMENT_METHOD_LIST, CACHE_TAG_ACCOUNT_PAYMENT_DETAILS],
    }),
    getTransactionList: builder.query({
      query: ({ businessId, payload }) => ({
        url: `${SERVER_PAYMENTS_URL}/api/portal/tenants/${businessId}/transaction/list`,
        method: 'POST',
        body: payload,
      }),
      providesTags: [CACHE_TAG_ACCOUNT_PAYMENT_TRANSACTION_LIST],
    }),
    startPendingPayment: builder.mutation({
      query: ({ businessId, payload }) => ({
        url: `${SERVER_PAYMENTS_URL}/api/portal/tenants/${businessId}/payment/pending`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [CACHE_TAG_ACCOUNT_PAYMENT_TRANSACTION_LIST],
    }),
    makePayment: builder.mutation({
      query: ({ businessId, payload }) => ({
        url: `${SERVER_PAYMENTS_URL}/api/portal/tenants/${businessId}/payment`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [CACHE_TAG_ACCOUNT_PAYMENT_TRANSACTION_LIST, CACHE_TAG_ACCOUNT_PAYMENT_METHOD_LIST],
    }),
    getPaymentStatus: builder.query({
      query: ({ businessId }) => ({
        url: `${SERVER_PAYMENTS_URL}/api/portal/tenants/${businessId}/provider/status`,
        method: 'GET',
      }),
    }),
  }),
})

export const {
  useGetPGUserInfoQuery,
  useLazyGetPGUserInfoQuery,
  useLazyGetIsSurchargeAppliedQuery,
  useLazyGetSurchargeRateQuery,
  useLazyGetAllPaymentMethodsQuery,
  useAddPaymentMethodMutation,
  useRemovePaymentMethodMutation,
  useLazyGetTransactionListQuery,
  useStartPendingPaymentMutation,
  useMakePaymentMutation,
  useLazyGetPaymentStatusQuery,
  useGetPaymentProviderQuery,
  useGetFortisClientTokenQuery,
  useLazyGetFortisClientTokenQuery,
  useCreateFortisTransactionIntentionMutation,
  useCreateFortisTicketIntentionMutation,
} = extendedApi

export const getPGUserInfoSlice = extendedApi.endpoints.getPGUserInfo.select()
